'use client';

import { faPaperPlane, faRedo, faTimes, faTrash, faUndo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useApi } from '@hooks/useApi';
import { motion } from 'framer-motion';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { useClubSlug } from './Clubs/hooks';
import { Button, StyledInput, Tooltip } from './ui';
import { renderMarkdown } from './ui/Chatbot/RenderText';
import KiLabel from './ui/KiLabel';
import SwitchingText from './ui/SwitchingText';

type Message = { role: 'user' | 'model'; text: string };

interface ExpandedChatProps {
    onClose: () => void;
    name?: string;
    conversationId?: string;
}

const LOADING_TEXTS = [
    'Wecke den Algorithmus …',
    'Flüstere der KI …',
    'Streichle Neuronen …',
    'Bits machen Pause …',
    'Modell orakelt …'
];

const createConversationId = () => uuidv4();

export const ExpandedChat: FC<ExpandedChatProps> = ({ name, onClose }) => {
    const { getValues, setValue } = useFormContext();

    const conversationIdRef = useRef<string | null>(null);
    const [previousFormValue, setPreviousFormValue] = useState<string[] | undefined>(undefined);
    const [futureFormValue, setFutureFormValue] = useState<string[]>([]);
    const [lastTip, setLastTip] = useState<string | null>(null);
    const [lastTipPrompt, setLastTipPrompt] = useState<string | null>(null);

    const [messages, setMessages] = useState<Message[]>([
        { role: 'model', text: 'Hi, ich bin dein KI-Assistent. Wobei darf ich helfen?' }
    ]);

    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const listRef = useRef<HTMLDivElement>(null);

    const formValue = getValues(name);
    const exampleQuestions = useMemo(
        () => [
            'Formuliere eine freundliche Einleitung.',
            'Füge eine Liste mit Vorteilen hinzu.',
            "Mach's einfacher verständlich.",
            'Kürze den Text auf 3 Sätze.'
        ],
        []
    );

    useEffect(() => {
        listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
    }, [messages, loading]);

    const clubSlug = useClubSlug();
    const api = useApi();
    const apiUrl = (process.env.NEXT_PUBLIC_BACKEND_HOST || '') + `ai/genereteText/${clubSlug}`;

    const send = async (prompt?: string) => {
        const question = (prompt ?? input).trim();
        if (!question || loading) return;

        setLoading(true);
        setInput('');
        const nextMessages = [...messages, { role: 'user', text: question }];
        // @ts-ignore
        setMessages(nextMessages);

        const history: { userQuestion: string }[] = [];
        for (let i = 0; i < nextMessages.length - 1; i++) {
            if (nextMessages[i].role === 'user' && nextMessages[i + 1]?.role === 'model') {
                history.push({
                    userQuestion: nextMessages[i].text
                });
            }
        }
        setPreviousFormValue([...(previousFormValue || []), formValue]);

        setFutureFormValue([]);

        await api({
            url: apiUrl,
            method: 'POST',
            data: {
                prompt: question,
                history: JSON.stringify(history),
                value: formValue || '',
                conversationId: conversationIdRef.current
            },
            onSuccess: (data: any) => {
                if (data?.error) {
                    setMessages((prev) => [
                        ...prev,
                        {
                            role: 'model',
                            text: 'Etwas ist schiefgelaufen. Bitte versuche es erneut.'
                        }
                    ]);
                    setLastTip(null);
                    setLastTipPrompt(null);
                    return;
                }

                setMessages((prev) => [...prev, { role: 'model', text: data.answer }]);
                setLastTip(typeof data?.tip === 'string' ? data.tip : null);
                setLastTipPrompt(typeof data?.tipPrompt === 'string' ? data.tipPrompt : null);

                setValue(name, data.updatedValue || formValue);
            },
            onError: (error: any) => {
                setMessages((prev) => [
                    ...prev,
                    {
                        role: 'model',
                        text: error?.response
                            ? 'Etwas ist schiefgelaufen. Bitte versuche es erneut.'
                            : 'Ich kann gerade keine Verbindung herstellen. Bitte später erneut versuchen.'
                    }
                ]);

                setLastTip(null);
                setLastTipPrompt(null);
            },
            onFinish: () => setLoading(false)
        });
    };

    function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void send();
        }
    }

    const handleUndo = () => {
        if (!previousFormValue || previousFormValue.length === 0) return;

        const lastValue = previousFormValue[previousFormValue.length - 1];
        const currentValue = getValues(name);
        setValue(name, lastValue);
        setPreviousFormValue((prev) => prev?.slice(0, -1));

        setFutureFormValue((prev) => [...prev, currentValue]);
    };

    const handleRedo = () => {
        if (futureFormValue.length === 0) return;

        const nextValue = futureFormValue[futureFormValue.length - 1];
        const currentValue = getValues(name);

        setValue(name, nextValue);
        setFutureFormValue((prev) => prev.slice(0, -1));
        setPreviousFormValue((prev) => [...(prev || []), currentValue]);
    };

    return (
        <motion.div
            className={`tw-z-[9999] tw-flex tw-h-full tw-max-h-full tw-flex-col tw-overflow-hidden tw-bg-[#f4f4f5]`}
            initial={{ width: '50%', x: '15vw' }}
            animate={{ width: '100%', x: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
        >
            <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-2">
                <div className="tw-flex tw-flex-col">
                    <KiLabel className="tw-text-base tw-text-accents-9">Ki Assistent</KiLabel>
                </div>
                <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-1">
                    <Tooltip tooltip="Letzte Version">
                        <Button
                            isDisabled={!(previousFormValue && previousFormValue.length !== 0)}
                            flat
                            size="xs"
                            onClick={handleUndo}
                            disabled={loading}
                            className="!tw-h-7 !tw-w-7 !tw-rounded-full"
                        >
                            <FontAwesomeIcon icon={faUndo} />
                        </Button>
                    </Tooltip>
                    <Tooltip trigger="hover" tooltip="Nächste Version">
                        <Button
                            isDisabled={futureFormValue.length === 0}
                            flat
                            size="xs"
                            onClick={handleRedo}
                            disabled={loading}
                            className="!tw-h-7 !tw-w-7 !tw-rounded-full"
                        >
                            <FontAwesomeIcon icon={faRedo} />
                        </Button>
                    </Tooltip>

                    <Button
                        flat
                        color="error"
                        size="xs"
                        onClick={() => {
                            setMessages([
                                {
                                    role: 'model',
                                    text: 'Hi, ich bin dein KI-Assistent. Wobei darf ich helfen?'
                                }
                            ]);
                            setLastTip(null);
                            setLastTipPrompt(null);
                            setFutureFormValue([]);
                            setPreviousFormValue([]);
                            conversationIdRef.current = createConversationId();
                        }}
                        disabled={loading || messages.length === 0}
                        className="!tw-h-7 !tw-w-7 !tw-rounded-full focus:tw-ring-error/80"
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </Button>

                    <Button
                        color="secondary"
                        onClick={onClose}
                        flat
                        size="xs"
                        className="!tw-h-7 !tw-w-7 !tw-rounded-full"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </Button>
                </div>
            </div>

            <div ref={listRef} className="tw-flex-1 tw-space-y-3 tw-overflow-y-auto tw-p-4">
                {messages.map((m, idx) => (
                    <div key={idx} className={`tw-flex ${m.role === 'user' ? 'tw-justify-end' : 'tw-justify-start'}`}>
                        <div
                            className={`tw-max-w-[80%] tw-rounded-lg tw-px-3 tw-py-1 tw-text-xs ${
                                m.role === 'user'
                                    ? 'tw-rounded-br-none tw-bg-gradient-to-br tw-from-success tw-to-success/90 tw-text-white tw-shadow-lg'
                                    : 'tw-rounded-bl-none tw-bg-white tw-text-accents-8 tw-shadow-sm tw-ring-1 tw-ring-black/5'
                            }`}
                        >
                            {renderMarkdown(m.text)}
                        </div>
                    </div>
                ))}

                {loading && <AiLoadingBubble />}

                {messages.length === 1 && !loading && (
                    <div className="tw-grid tw-gap-2">
                        {exampleQuestions.map((q) => (
                            <Button size="xs" color="light" flat key={q} type="button" onClick={() => send(q)}>
                                {q}
                            </Button>
                        ))}
                    </div>
                )}
                {!loading && lastTip && lastTipPrompt && (
                    <div className="tw-mt-2 tw-rounded-lg tw-bg-white tw-p-3 tw-text-xs tw-shadow-sm tw-ring-1 tw-ring-black/5">
                        <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                            <div className="tw-text-accents-8">
                                <div className="tw-mb-1 tw-font-semibold tw-text-accents-9">Tipp</div>
                                <div>{lastTip}</div>
                            </div>

                            <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-1">
                                <Tooltip tooltip="Tipp automatisch anwenden">
                                    <Button
                                        size="xs"
                                        color="secondary"
                                        flat
                                        disabled={loading}
                                        onClick={() => send(lastTipPrompt)}
                                    >
                                        Anwenden
                                    </Button>
                                </Tooltip>

                                <Tooltip tooltip="Tipp ausblenden">
                                    <Button
                                        size="xs"
                                        color="light"
                                        flat
                                        disabled={loading}
                                        onClick={() => {
                                            setLastTip(null);
                                            setLastTipPrompt(null);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </Button>
                                </Tooltip>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="tw-flex tw-items-center tw-gap-2 tw-border-t tw-p-2 tw-px-4 tw-backdrop-blur">
                <StyledInput
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    disabled={loading}
                    size="xs"
                    fullWidth
                    bordered
                    placeholder={'Schreib hier, was ich ändern soll…'}
                />
                <Button size="xs" color="secondary" onClick={() => send()} disabled={loading || !input.trim()}>
                    <FontAwesomeIcon icon={faPaperPlane} />
                </Button>
            </div>
        </motion.div>
    );
};

const AiLoadingBubble: FC = () => {
    const dots = [0, 1, 2];

    return (
        <div className="tw-flex tw-justify-start">
            <div className="tw-max-w-[80%] tw-rounded-lg tw-rounded-bl-none tw-bg-white tw-px-3 tw-py-2 tw-text-xs tw-text-success tw-shadow-sm tw-ring-1 tw-ring-black/5">
                <div className="tw-flex tw-items-center tw-gap-2">
                    <div className="tw-flex tw-items-center tw-gap-1">
                        {dots.map((i) => (
                            <motion.span
                                key={i}
                                className="tw-inline-block tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-success"
                                initial={{ y: 0, opacity: 0.6 }}
                                animate={{ y: [0, -4, 0], opacity: [0.6, 1, 0.6] }}
                                transition={{
                                    duration: 0.7,
                                    repeat: Infinity,
                                    repeatType: 'loop',
                                    ease: 'easeInOut',
                                    delay: i * 0.12
                                }}
                            />
                        ))}
                    </div>

                    <div className="tw-w-[200px] tw-text-center">
                        <SwitchingText texts={LOADING_TEXTS} />
                    </div>
                </div>

                {/* subtle shimmer line */}
                <div className="tw-mt-2 tw-h-1 tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-successLight">
                    <motion.div
                        className="tw-h-full tw-w-1/2 tw-rounded-full tw-bg-success/60"
                        initial={{ x: '-100%' }}
                        animate={{ x: '200%' }}
                        transition={{ duration: 1.1, repeat: Infinity, ease: 'linear' }}
                    />
                </div>
            </div>
        </div>
    );
};
